/**
 * MediaStream level meter.
 *
 * Computes an output level (`0..1`) from a live `MediaStream` via an
 * `AnalyserNode`, without routing the stream to the speakers (a zero-gain sink
 * keeps the graph pulling). Used by the WebRTC path, where the agent's audio is
 * a remote `MediaStream` played through an `<audio>` element rather than the
 * {@link AudioPlayback} PCM queue, but the visualizer still needs a level.
 */

import { rms } from "./pcm.js";

export interface StreamLevelMeter {
  /** Sample the current output level (`0..1`). Cheap; call from a render loop. */
  getLevel(): number;
  /** Detach and release the analyser graph (does not stop the stream). */
  stop(): void;
}

export interface StreamLevelMeterOptions {
  /** Reuse an existing `AudioContext` instead of creating one. */
  audioContext?: AudioContext;
  /** FFT size for the analyser. Default `1024`. */
  fftSize?: number;
}

/** Attach a level meter to a `MediaStream` (e.g. a WebRTC remote audio stream). */
export function createStreamLevelMeter(
  stream: MediaStream,
  options: StreamLevelMeterOptions = {},
): StreamLevelMeter {
  const ownsContext = options.audioContext === undefined;
  const ctx = options.audioContext ?? new AudioContext();

  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = options.fftSize ?? 1024;
  // Zero-gain sink: keeps the analyser pulling audio without double-playing it.
  const sink = ctx.createGain();
  sink.gain.value = 0;
  source.connect(analyser);
  analyser.connect(sink);
  sink.connect(ctx.destination);

  const buffer = new Float32Array(analyser.fftSize);

  return {
    getLevel(): number {
      analyser.getFloatTimeDomainData(buffer);
      return rms(buffer);
    },
    stop(): void {
      source.disconnect();
      analyser.disconnect();
      sink.disconnect();
      if (ownsContext) void ctx.close();
    },
  };
}
